import clone from 'clone';
import { Subject } from 'rxjs';
import { Dao } from '../dao';
import { FlowyFoundation } from '../flowy';
import { Flux } from '../flux';
import {
  IGizmo,
  IGizmoCallbacks,
  IGizmoData,
  IGizmoEvent,
  IGizmoFeatures,
  ISingleFeature,
  IError,
  TGizmoOperation,
  TScreenColumns,
  TCustomValidations,
  IPlatformCallbacks,
  ISubscription,
  IGizmoHandlers,
  IGizmoAncestor,
  IRootGizmoCallbacks,
  ISearchResult,
  ISearchFeatures,
} from '../types';
import { snov } from '../utils';
import {
  dispatchGizmoOperation,
  handleGizmoEvents,
  init,
  selectGizmoSearchResult,
  setData,
  serverScript,
} from './gizmo-functions';
import RootGizmo from './root-gizmo';
import {
  commonValidations,
  localValidation,
  serverValidation,
  textFieldValidations,
} from './validations';

interface IGizmoConstructor {
  /**
   * Gizmo's configuration object
   */
  config: IGizmo;
  /**
   * Callbacks used by the Gizmo to communicate with the RootGizmo
   */
  callbacks: IGizmoCallbacks;
  /**
   * Callbacks used by the RootGizmo to communicate with the Foundation layer
   */
  rootGizmoCallbacks: IRootGizmoCallbacks;
  /**
   * Stream of GizmoEvents
   */
  events: Subject<IGizmoEvent>;
  /**
   * Flowy Foundation instance
   */
  foundation?: FlowyFoundation;
  /**
   * Objects callback functions used to interact with functionality exposed by custom hooks
   */
  platformCallbacks?: IPlatformCallbacks;
  /**
   * Instance of Dao
   */
  dao?: Dao;
  /**
   * Parameters arriving from the platform layer
   */
  params: any;
  /**
   * List of ancestors of the Gizmo
   */
  ancestors: IGizmoAncestor[];
}

interface ISendGizmoOperationParams {
  operation: TGizmoOperation;
  params?: {
    /**
     * In case the operation is a navigation operation we can put the path in here
     */
    path?: string;
    fid?: string;
    rgfid?: string;
    id?: string;
    /**
     * If the operation is keyboard.key we can put the key in here
     */
    key?: string;
  };
}

interface ISetFeaturesParams {
  /**
   * List of single features disaggregated by path and value
   */
  singleFeatures: ISingleFeature[];
}

class Gizmo {
  /**
   * Gizmo's configuration
   */
  private _config: IGizmo;
  /**
   * The data consumed by the Gizmo in Flowy format
   */
  private _data?: IGizmoData | IGizmoData[];
  /**
   * Dinamically generated Gizmo features
   */
  private _features: IGizmoFeatures;
  /**
   * In case the Gizmo has children the list will be here
   */
  private _gizmos?: (Gizmo | RootGizmo)[];
  /**
   * The gizmo will store the parent columns configuration, this is done so the UI
   * layer can use this values to calculate the correct width of the gizmo
   */
  private _columns: TScreenColumns;
  /**
   * This list will be used to store the path of ancestors beginning with the RootGizmo
   */
  private _ancestors: IGizmoAncestor[];
  /**
   * Callbacks used by the Gizmo to communicate with the Root Gizmo
   */
  private _callbacks: IGizmoCallbacks;
  /**
   * Callbacks used by the RootGizmo to communicate with the Foundation layer
   */
  private _rootGizmoCallbacks: IRootGizmoCallbacks;
  /**
   * Array of subscriptions needed by the Gizmo
   */
  private _subscriptions: ISubscription[];
  /**
   * Instance of Dao (data access object)
   */
  private _dao?: Dao;
  /**
   * Instance of the Gizmo's binder
   */
  private _binder?: any;
  /**
   * Reference to the stream of events that will be arriving from the RootGizmo
   */
  private _events: Subject<IGizmoEvent>;
  /**
   * Flux instance for the Gizmo, maybe we should have a global Flux instance?
   */
  private _flux?: Flux;
  /**
   * Flowy Foundation instance
   */
  private _foundation?: FlowyFoundation;
  /**
   * Callbacks used to interact with custom hook functionality
   */
  private _platformCallbacks?: IPlatformCallbacks;
  /**
   * Function with custom validations that will be provided by a custom Gizmo
   */
  private _customValidations?: TCustomValidations;
  /**
   * Parametes arriving from the platform layer
   */
  private _params: any;
  /**
   * List of validation errors
   */
  private _errors: IError[];
  /**
   * This variable is used as cache to store the search results and the data associated to it, this
   * data is the index element where the search result was originated from. We do this because
   * we need the data in order to build the gizmo event which will be used to updated other gizmos
   * data
   */
  private _searchResultsWithData: {
    searchResult: ISearchResult;
    // data: IFilledFormRowItem;
    data: any;
  }[];
  /**
   * This also is a cache variable, basically we store the search features that we're sending to
   * the platform layer. We did this in order to identify when the user has deleted some characters
   * which trigger clearing the updated gizmos.
   */
  private _gizmoSearchFeatures: ISearchFeatures;
  /**
   * Validation errors that could be added by anyone with access to the gizmo.
   * Right now it's only used when selecting a search result that has a status that's
   * configured as being an error.
   */
  private _extraErrors: IError[];

  /**
   * Observables used by the UI layer in order to receive information from the core layer
   */
  public data: Subject<IGizmoData | IGizmoData[] | undefined>;
  public features: Subject<IGizmoFeatures>;
  public errors: Subject<IError[]>;
  public gizmoSearchFeatures: Subject<ISearchFeatures>;

  constructor({
    config,
    callbacks,
    rootGizmoCallbacks,
    events,
    foundation,
    platformCallbacks,
    dao,
    params,
    ancestors,
  }: IGizmoConstructor) {
    this._config = config;
    this._callbacks = callbacks;
    this._rootGizmoCallbacks = rootGizmoCallbacks;
    this._events = events;
    this._platformCallbacks = platformCallbacks;
    this._foundation = foundation;
    this._dao = dao;
    this._params = params;
    this._ancestors = clone(ancestors);

    // Default columns
    this._columns = {
      xl: 1,
      l: 1,
      m: 1,
      s: 1,
    };

    // Default features
    this._features = {
      label: config.label,
      number: config.number,
      display: true,
      editable: true,
      required: false,
      loading: false,
      displayLabel: true,
      processing: false,
    };
    this._subscriptions = [];

    this.data = new Subject<IGizmoData | IGizmoData[] | undefined>();
    this.features = new Subject<IGizmoFeatures>();
    this.errors = new Subject<IError[]>();

    this._gizmos = [];
    this._errors = [];
    this._extraErrors = [];

    // Search
    this._searchResultsWithData = [];
    this._gizmoSearchFeatures = { processing: false };
    this.gizmoSearchFeatures = new Subject<ISearchFeatures>();
  }

  public init = (): Promise<Gizmo> => {
    return new Promise<Gizmo>(async resolve => {
      // Subscribe to the Gizmo events stream
      this._events.subscribe(this._handleGizmoEvents);

      const { features, binder, subscriptions } = await init({
        gizmo: this,
        context: {
          config: this._config,
          callbacks: this._callbacks,
          rootGizmoCallbacks: this._rootGizmoCallbacks,
          defaultFeatures: this._features,
          setData: this.setData,
          events: this._events,
          params: this._params,
          dao: this._dao,
          ancestors: this._ancestors,
          foundation: this._foundation,
          setFlux: flux => {
            this._flux = flux;
          },
          setGizmos: gizmos => {
            this._gizmos = gizmos;
          },
        },
      });

      const handlers: IGizmoHandlers = {
        validationsHDLR: this.validate,
      };
      this._callbacks.setHandlersCB({
        gizmoHandlers: handlers,
        fid: this._config.fid,
        ugid: this._config.fid,
        uid: this._config.uid,
      });

      this._features = features;
      this._binder = binder;
      this._subscriptions = subscriptions;

      resolve(this);
    });
  };

  /**
   * Get the gizmo configuration
   * @returns {IGizmo} the gizmo configuration
   */
  public getConfig = (): IGizmo => {
    return this._config;
  };

  /**
   * Get Flux instance
   * @returns {Flux}
   */
  public getFlux = (): Flux | undefined => {
    return this._flux;
  };

  /**
   * Get FlowyFoundation instance
   * @returns {FlowyFoundation}
   */
  public getFoundation = (): FlowyFoundation | undefined => {
    return this._foundation;
  };

  /**
   * Get the params comming from the platform layer
   *
   * @returns {any} params
   */
  public getParams = (): any => {
    return this._params;
  };

  /**
   * Get the gizmo ancestors, this method is going to be used by the binders
   * @returns {IGizmoAncestor[]}
   */
  public getAncestors = (): IGizmoAncestor[] => {
    return this._ancestors;
  };

  /**
   * Get the stream used to listen to events from the RootGizmo
   * @returns {Subject<IGizmoEvent>}
   */
  public getEvents = (): Subject<IGizmoEvent> => {
    return this._events;
  };

  /**
   * Set extra errors, right now it's only used when selecting a search result that
   * has a status that's configured as being an error.
   *
   * @param {IError[]} errors
   */
  public setExtraErrors = (extraErrors: IError[]): void => {
    this._extraErrors = extraErrors;

    this.errors.next(extraErrors);
  };

  /**
   * Get the error stream
   *
   * @returns {Subject<IError[]>}
   */
  public getErrors = (): Subject<IError[]> => {
    return this.errors;
  };

  /**
   * Get the child gizmos, not all gizmos can have child gizmos
   * @returns {(RootGizmo | Gizmo)[]} child gizmos if they exist
   */
  public getGizmos = (): (RootGizmo | Gizmo)[] | undefined => {
    return this._gizmos;
  };

  /**
   * Get gizmo callbacks
   * @returns {IGizmoCallbacks}
   */
  public getCallbacks = (): IGizmoCallbacks => {
    return this._callbacks;
  };

  /**
   * Get root gizmo callbacks
   * @returns {IRootGizmoCallbacks}
   */
  public getRootGizmoCallbacks = (): IRootGizmoCallbacks => {
    return this._rootGizmoCallbacks;
  };

  /**
   * Function used by a 'row' Gizmo in order to pass the configured columns to the
   * child Gizmos
   *
   * @param columns columns configured on the row (the parent)
   */
  public setColumns = (columns: TScreenColumns) => {
    this._columns = columns;
  };

  /**
   * It will be used by the custom hook in order to pass the configured columns
   * to the platform layer
   * @returns columns configured by the parent Row Gizmo
   */
  public getColumns = (): TScreenColumns => {
    return this._columns;
  };

  /**
   * Set Gizmo features object
   *
   * @param singleFeatures list of paths and values we're going to set
   */
  public setFeatures = ({ singleFeatures }: ISetFeaturesParams): void => {
    for (const sf of singleFeatures) {
      const { path, value } = sf;

      snov(this._features, path, value);
    }

    this.features.next(this._features);
  };

  /**
   * Return the Gizmo features
   * @returns {IGizmoFeatures} Gizmo features
   */
  public getFeatures = (): IGizmoFeatures => {
    return this._features;
  };

  /**
   * Get the Gizmo's binder
   * @returns a Binder if it exists
   */
  public getBinder = (): any | undefined => {
    return this._binder;
  };

  /**
   * Get the Dao instance
   * @returns {Dao | undefined} Dao instance
   */
  public getDao = (): Dao | undefined => {
    return this._dao;
  };

  /**
   * Set the Gizmo's local data, notify the observer and the RootGizmo
   * @param data data in GizmoData format
   */
  public setData = (data?: IGizmoData | IGizmoData[]): Promise<void> => {
    return new Promise<void>(async resolve => {
      const previousData = clone(this._data);
      //! Should this be a promise???
      this._data = data;
      this.data.next(data);

      // TODO: Send data to the Observable

      // TODO: run validations if errors are present, i.e., Gizmo has been validated
      if (this._errors.length > 0) {
        await this.validate();
      }

      await this._callbacks.setGizmoDataCB({
        fid: this._config.fid,
        data,
      });

      if (data && this._dao) {
        await setData({
          data,
          previousData,
          context: {
            gizmo: this,
            dao: this._dao,
            gizmoSearchFeatures: this._gizmoSearchFeatures,
            setGizmoSearchFeatures: gizmoSearchFeatures => {
              this._gizmoSearchFeatures = gizmoSearchFeatures;
              this.gizmoSearchFeatures.next(gizmoSearchFeatures);
            },
            setSearchResultsWithData: searchResultsWithData => {
              this._searchResultsWithData = searchResultsWithData;
            },
          },
        });
      }

      resolve();
    });
  };

  /**
   * Get the GizmoData
   * @returns {IGizmoData} the gizmo data object
   */
  public getData = (): IGizmoData | IGizmoData[] | undefined => {
    return this._data;
  };

  /**
   * Get gizmo search features
   * @returns
   */
  public getGizmoSearchFeatures = (): ISearchFeatures => {
    return this._gizmoSearchFeatures;
  };

  /**
   * Function used by the platform layer in order to notify the core a search result has been
   * selected
   * @param {ISearchResult} searchResult user selected search result
   * @returns
   */
  public selectGizmoSearchResult = (
    searchResult: ISearchResult
  ): Promise<void> => {
    return new Promise<void>(async resolve => {
      await selectGizmoSearchResult({
        searchResult,
        context: {
          gizmo: this,
          callbacks: this._callbacks,
          searchResultsWithData: this._searchResultsWithData,
          dao: this._dao,
        },
      });

      resolve();
    });
  };

  /**
   * Set a function with custom validations
   *
   * @param customValidations function with the custom validations
   */
  public setCustomValidations = (customValidations: TCustomValidations) => {
    this._customValidations = customValidations;
  };

  /**
   * Run a script on the server
   */
  public serverScript = async () => {
    await serverScript({
      context: {
        gizmo: this,
        callbacks: this._callbacks,
      },
    });
  };

  /**
   * Validate the gizmo data with the configured validations
   * @returns {Promise<IError[]>} validation errors
   */
  public validate = (withOutServer?: boolean): Promise<IError[]> => {
    return new Promise<IError[]>(async resolve => {
      let errors: IError[] = [];

      const commonErrors = commonValidations({
        config: this._config,
        features: this._features,
        data: this._data,
      });

      if (commonErrors.length > 0) {
        errors.push.apply(errors, commonErrors);
      }

      if (this._config.type === 'text_field') {
        const textFieldErrors = textFieldValidations({
          config: this._config,
          data: this._data,
        });

        if (textFieldErrors.length > 0) {
          errors.push.apply(errors, textFieldErrors);
        }
      }

      if (this._config.type === 'custom' && this._customValidations) {
        const customErrors = await this._customValidations({
          gizmoData: this._data,
        });

        if (customErrors.length > 0) {
          errors.push.apply(errors, customErrors);
        }
      }

      if (this._extraErrors.length > 0) {
        errors.push.apply(errors, this._extraErrors);
      }

      if (this._config.ops?.localValidation?.flux && this._flux) {
        const localValidationErrors = await localValidation({
          context: {
            gizmo: this,
            flux: this._flux,
          },
        });

        if (localValidationErrors.length > 0) {
          errors.push.apply(errors, localValidationErrors);
        }
      }

      if (this._binder?.binderValidate && errors.length === 0) {
        const binderErrors = await this._binder.binderValidate();
        if (binderErrors.length > 0) {
          errors.push.apply(errors, binderErrors);
        }
      }

      if (
        this._config.ops?.serverValidation?.flux &&
        !withOutServer &&
        errors.length === 0
      ) {
        const serverValidationErrors = await serverValidation({
          context: {
            gizmo: this,
            callbacks: this._callbacks,
          },
        });

        if (serverValidationErrors.length > 0) {
          errors.push.apply(errors, serverValidationErrors);
        }
      }

      this._errors = errors;
      this.errors.next(errors);

      resolve(
        errors.map(e => ({
          ...e,
          features: this._features,
          ancestors: this._ancestors,
        }))
      );
    });
  };

  /**
   * Function that receives the events from the stream originated on the RootGizmo
   *
   * @param {IGizmoEvent} gizmoEvent gizmo event arriving on the stream
   * @returns
   */
  private _handleGizmoEvents = (gizmoEvent: IGizmoEvent): Promise<void> => {
    return new Promise<void>(async resolve => {
      await handleGizmoEvents({
        gizmoEvent,
        context: {
          gizmo: this,
          subscriptions: this._subscriptions,
          binder: this._binder,
          flux: this._flux,
          currentFeatures: this._features,
        },
      });
      resolve();
    });
  };

  /**
   * Method that will be used by binders in order to send instructions to the core layer
   *
   * @param {TGizmoOperation} operation gizmo operation dispatched in order to be processed
   * @param {any} params
   * @returns Promise<void>
   */
  public dispatchGizmoOperation = ({
    operation,
    params,
  }: ISendGizmoOperationParams): Promise<any> => {
    return new Promise<any>(async resolve => {
      resolve(
        await dispatchGizmoOperation({
          operation,
          params,
          context: {
            callbacks: this._callbacks,
            config: this._config,
            platformCallbacks: this._platformCallbacks,
            dao: this._dao,
            ancestors: this._ancestors,
          },
        })
      );
    });
  };
}

export default Gizmo;
